import { type Dispatch, type SetStateAction, memo, useMemo } from 'react';
import { Badge, Text } from '@mantine/core';
import type { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import type { InventoryStockReportItem } from 'washday-sdk';
import Table from '../../../components/Table';

export default memo(function TableView({
  tableInfoData,
  totalRowsCount,
  isLoading,
  isError,
  pagination,
  setPagination
}: {
  tableInfoData: InventoryStockReportItem[];
  totalRowsCount: number;
  isLoading: boolean;
  isError: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
}) {
  const { t, i18n } = useTranslation('ReportsPage');
  const language = i18n.resolvedLanguage || i18n.language;
  const columns = useMemo<MRT_ColumnDef<InventoryStockReportItem>[]>(() => {
    const numberFormat = new Intl.NumberFormat(language, { maximumFractionDigits: 3 });
    return [
      {
        accessorKey: 'storeName',
        header: t('ReportsPage:inventoryStockReport.headers.store')
      },
      {
        id: 'productName',
        accessorFn: (row) =>
          row.productName || t('ReportsPage:inventoryStockReport.unavailableProduct'),
        header: t('ReportsPage:inventoryStockReport.headers.product')
      },
      {
        id: 'sku',
        accessorFn: (row) => row.sku || '—',
        header: t('ReportsPage:inventoryStockReport.headers.sku')
      },
      {
        id: 'stock',
        accessorFn: (row) => numberFormat.format(row.stock),
        header: t('ReportsPage:inventoryStockReport.headers.stock')
      },
      {
        id: 'alertOnStock',
        accessorFn: (row) =>
          row.alertOnStock == null ? '—' : numberFormat.format(row.alertOnStock),
        header: t('ReportsPage:inventoryStockReport.headers.threshold')
      },
      {
        accessorKey: 'status',
        header: t('ReportsPage:inventoryStockReport.headers.status'),
        Cell: ({ row: { original: item } }) => (
          <Badge
            variant="light"
            color={item.status === 'low' ? 'orange' : item.status === 'normal' ? 'green' : 'gray'}
          >
            {t(`ReportsPage:inventoryStockReport.statuses.${item.status}`)}
          </Badge>
        )
      }
    ];
  }, [language, t]);

  return (
    <Table
      tableColumns={columns}
      tableData={tableInfoData}
      totalRowsCount={totalRowsCount}
      numPages={Math.max(1, Math.ceil(totalRowsCount / pagination.pageSize))}
      pagination={pagination}
      setPagination={setPagination}
      paginationIsLoading={isLoading}
      isError={isError}
      overrideTableConfigs={{
        renderEmptyRowsFallback: () => (
          <Text py="lg">{t('ReportsPage:inventoryStockReport.empty')}</Text>
        ),
        mantinePaginationProps: {
          showRowsPerPage: false,
          getControlProps: (control: string) => ({
            'aria-label': t(`ReportsPage:inventoryStockReport.pagination.${control}`)
          })
        }
      }}
    />
  );
});
